/**
 * JSON-RPC access for the browser wallet, through the same-origin proxy.
 *
 * The SDK client owns the wire format; this module adds the handful of
 * methods the wallet needs beyond pool reads (leaf discovery, blockhash and
 * fee inputs, submission and confirmation) with their responses checked
 * before anything downstream relies on them.
 */
import {
  createSolanaRpc,
  getSolanaAccounts,
  solanaAssociatedTokenAddress,
  solanaPubkeyBase58,
  type SolanaCommitment,
  type SolanaRpc,
  type SolanaTransactionLike,
} from "@stealthos/sdk";
import { ethers } from "ethers";
import { fail, sleep } from "./common";
import { withDeadline } from "../../utils/withDeadline";

export type { SolanaRpc };

/** Reads blockchain state directly even when every StealthOS service is unavailable. */
export function getDirectSolanaRecoveryRpc(
  network: { recoveryRpcUrl?: string; cluster: string },
  overrideUrl?: string,
): SolanaRpc {
  const url = new URL(
    overrideUrl ??
      network.recoveryRpcUrl ??
      (network.cluster === "devnet"
        ? "https://api.devnet.solana.com"
        : "https://api.mainnet-beta.solana.com"),
  );
  if (
    url.protocol !== "https:" ||
    url.username ||
    url.password ||
    /(^|\.)stealthos\.xyz$/i.test(url.hostname)
  )
    fail("Independent recovery requires a direct HTTPS Solana RPC URL");
  return createSolanaBrowserRpc(url.toString());
}

export function createSolanaBrowserRpc(url: string): SolanaRpc {
  return {
    request: <T>(method: string, args?: unknown[]) =>
      withDeadline(
        async (signal) => {
          for (let lagAttempt = 0; ; lagAttempt += 1) {
            try {
              return await createSolanaRpc(url, {
                fetch: async (target, init) => {
                  const options = {
                    ...init,
                    credentials: "same-origin" as const,
                    cache: "no-store" as const,
                    redirect: "error" as const,
                    signal,
                  };
                  // A fresh browser rebuilds the tree from public transaction history.
                  // Public devnet RPCs can throttle that scan. Retry only read methods;
                  // submitting a signed transaction retains its existing ambiguity rules.
                  let isRead = false;
                  try {
                    isRead = /^get[A-Z]/.test(
                      JSON.parse(String(init?.body)).method,
                    );
                  } catch {
                    // An unrecognized request must not be replayed automatically.
                  }
                  for (let attempt = 0; ; attempt += 1) {
                    const response = await fetch(target, options);
                    if (!isRead || response.status !== 429 || attempt >= 4)
                      return response;
                    const retryAfter = response.headers.get("retry-after");
                    let delay = 2000 * 2 ** attempt;
                    if (retryAfter) {
                      const seconds = Number(retryAfter);
                      const advised = Number.isFinite(seconds)
                        ? seconds * 1000
                        : Date.parse(retryAfter) - Date.now();
                      if (Number.isFinite(advised))
                        delay = Math.max(delay, advised);
                    }
                    if (delay > 30000) return response;
                    await response.body?.cancel();
                    await sleep(delay);
                  }
                },
              }).request<T>(method, args);
            } catch (error) {
              // Public RPCs can route consecutive reads to nodes at different
              // slots. Keep the minimum slot intact and briefly wait for a node
              // that has caught up; never retry a transaction submission here.
              if (
                signal.aborted ||
                !/^get[A-Z]/.test(method) ||
                lagAttempt >= 4 ||
                !(error instanceof Error) ||
                !/RPC_SLOT_BEHIND_MIN_CONTEXT_SLOT|minimum context slot has not been reached/i.test(
                  error.message,
                )
              )
                throw error;
              await sleep(250 * 2 ** lagAttempt);
            }
          }
        },
        "Solana RPC request timed out; retry recovery",
        undefined,
        90_000,
      ),
  };
}

export interface SolanaLatestBlockhash {
  blockhash: string;
  lastValidBlockHeight: number;
  slot: number;
}

function requireSafeNumber(value: unknown, label: string): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) {
    fail(`RPC returned an invalid ${label}`);
  }
  return value;
}

export async function getSolanaLatestBlockhash(
  rpc: SolanaRpc,
  commitment: SolanaCommitment = "confirmed",
): Promise<SolanaLatestBlockhash> {
  const result = await rpc.request<{
    context?: { slot?: number };
    value?: { blockhash?: string; lastValidBlockHeight?: number };
  }>("getLatestBlockhash", [{ commitment }]);
  const blockhash = result?.value?.blockhash;
  if (typeof blockhash !== "string" || !blockhash) {
    fail("RPC returned an invalid blockhash");
  }
  return {
    blockhash: solanaPubkeyBase58(blockhash, "blockhash"),
    lastValidBlockHeight: requireSafeNumber(
      result?.value?.lastValidBlockHeight,
      "lastValidBlockHeight",
    ),
    slot: requireSafeNumber(result?.context?.slot, "slot"),
  };
}

export async function getSolanaBlockHeight(
  rpc: SolanaRpc,
  commitment: SolanaCommitment = "confirmed",
): Promise<number> {
  return requireSafeNumber(
    await rpc.request<number>("getBlockHeight", [{ commitment }]),
    "block height",
  );
}

export type SolanaConfirmationStatus = "processed" | "confirmed" | "finalized";

export interface SolanaSignatureStatus {
  slot: number;
  err: unknown | null;
  confirmationStatus: SolanaConfirmationStatus | null;
}

export async function getSolanaSignatureStatuses(
  rpc: SolanaRpc,
  signatures: readonly string[],
): Promise<(SolanaSignatureStatus | null)[]> {
  const result = await rpc.request<{
    value?: Array<{
      slot?: number;
      err?: unknown;
      confirmationStatus?: string | null;
    } | null>;
  }>("getSignatureStatuses", [
    [...signatures],
    { searchTransactionHistory: true },
  ]);
  if (
    !Array.isArray(result?.value) ||
    result.value.length !== signatures.length
  ) {
    fail("RPC returned a malformed getSignatureStatuses response");
  }
  return result.value.map((status) => {
    if (!status) return null;
    const level = status.confirmationStatus;
    return {
      slot: requireSafeNumber(status.slot, "status slot"),
      err: status.err ?? null,
      confirmationStatus:
        level === "processed" || level === "confirmed" || level === "finalized"
          ? level
          : null,
    };
  });
}

export interface SolanaSignatureInfo {
  signature: string;
  slot: number;
  err: unknown | null;
  blockTime: number | null;
}

export async function getSolanaSignaturesForAddress(
  rpc: SolanaRpc,
  address: string,
  options: {
    before?: string;
    until?: string;
    limit?: number;
    commitment?: SolanaCommitment;
  } = {},
): Promise<SolanaSignatureInfo[]> {
  const result = await rpc.request<
    Array<{
      signature?: string;
      slot?: number;
      err?: unknown;
      blockTime?: number | null;
    }>
  >("getSignaturesForAddress", [
    solanaPubkeyBase58(address, "address"),
    {
      commitment: options.commitment ?? "finalized",
      limit: options.limit ?? 1000,
      ...(options.before ? { before: options.before } : {}),
      ...(options.until ? { until: options.until } : {}),
    },
  ]);
  if (!Array.isArray(result)) {
    fail("RPC returned a malformed getSignaturesForAddress response");
  }
  return result.map((entry) => {
    if (typeof entry?.signature !== "string" || !entry.signature) {
      fail("RPC returned a signature entry without a signature");
    }
    return {
      signature: entry.signature,
      slot: requireSafeNumber(entry.slot, "signature slot"),
      err: entry.err ?? null,
      blockTime: typeof entry.blockTime === "number" ? entry.blockTime : null,
    };
  });
}

export interface SolanaConfirmedTransaction extends SolanaTransactionLike {
  slot: number;
  blockTime: number | null;
}

export async function getSolanaTransaction(
  rpc: SolanaRpc,
  signature: string,
  commitment: SolanaCommitment = "finalized",
): Promise<SolanaConfirmedTransaction | null> {
  const result = await rpc.request<
    | (SolanaTransactionLike & { slot?: number; blockTime?: number | null })
    | null
  >("getTransaction", [
    signature,
    { encoding: "json", commitment, maxSupportedTransactionVersion: 0 },
  ]);
  if (result === null || result === undefined) return null;
  if (
    !result.transaction?.message ||
    !Array.isArray(result.transaction.message.accountKeys)
  ) {
    fail("RPC returned a malformed getTransaction response");
  }
  return {
    ...result,
    slot: requireSafeNumber(result.slot, "transaction slot"),
    blockTime: typeof result.blockTime === "number" ? result.blockTime : null,
  };
}

export async function sendSolanaTransaction(
  rpc: SolanaRpc,
  transaction: Uint8Array,
  options: { preflightCommitment?: SolanaCommitment } = {},
): Promise<string> {
  const signature = await rpc.request<string>("sendTransaction", [
    ethers.encodeBase64(transaction),
    {
      encoding: "base64",
      preflightCommitment: options.preflightCommitment ?? "confirmed",
      maxRetries: 3,
    },
  ]);
  if (typeof signature !== "string" || !signature) {
    fail("RPC returned no transaction signature");
  }
  return signature;
}

export async function getSolanaBalance(
  rpc: SolanaRpc,
  address: string,
  commitment: SolanaCommitment = "confirmed",
): Promise<bigint> {
  const result = await rpc.request<{ value?: number }>("getBalance", [
    solanaPubkeyBase58(address, "address"),
    { commitment },
  ]);
  return BigInt(requireSafeNumber(result?.value, "balance"));
}

/** SPL token balance of the owner's associated account; zero when it does not exist. */
export async function getSolanaTokenBalance(
  rpc: SolanaRpc,
  owner: string,
  mint: string,
  tokenProgram: string,
  commitment: SolanaCommitment = "confirmed",
): Promise<bigint> {
  const account = solanaAssociatedTokenAddress(owner, mint, tokenProgram);
  const { accounts } = await getSolanaAccounts(rpc, [account], commitment);
  const info = accounts[0];
  if (!info) return 0n;
  if (info.owner !== tokenProgram || info.data.length < 72) {
    fail("associated token account is not a token account");
  }
  // SPL token account layout: mint (32) | owner (32) | amount u64 LE (8) ...
  let amount = 0n;
  for (let index = 7; index >= 0; index -= 1) {
    amount = (amount << 8n) | BigInt(info.data[64 + index]);
  }
  return amount;
}

export class SolanaTransactionFailedError extends Error {
  constructor(signature: string, error: unknown) {
    super(`transaction ${signature} failed on chain: ${JSON.stringify(error)}`);
    this.name = "SolanaTransactionFailedError";
  }
}

/** Check history again after finalized height passes expiry, avoiding a status/height race. */
export async function isSolanaSignatureExpired(
  rpc: SolanaRpc,
  signature: string,
  lastValidBlockHeight: number,
): Promise<boolean> {
  if ((await getSolanaBlockHeight(rpc, "finalized")) <= lastValidBlockHeight)
    return false;
  const [status] = await getSolanaSignatureStatuses(rpc, [signature]);
  return status === null;
}

export class SolanaTransactionExpiredError extends Error {
  readonly signature: string;

  constructor(signature: string) {
    super(
      "Solana JoinSplit V2 wallet: the transaction's blockhash expired before it was confirmed; it can no longer land",
    );
    this.name = "SolanaTransactionExpiredError";
    this.signature = signature;
  }
}

const CONFIRMATION_RANK: Record<SolanaConfirmationStatus, number> = {
  processed: 0,
  confirmed: 1,
  finalized: 2,
};

/**
 * Wait for a signature to reach `commitment`. A transaction with no status
 * once the chain has passed its last valid block height can never land and
 * is reported as expired; one that has any status keeps being polled until
 * it reaches the requested level or fails.
 */
export async function confirmSolanaSignature(
  rpc: SolanaRpc,
  params: {
    signature: string;
    lastValidBlockHeight?: number;
    commitment?: SolanaConfirmationStatus;
    pollMs?: number;
    timeoutMs?: number;
    signal?: AbortSignal;
    now?: () => number;
  },
): Promise<{ slot: number }> {
  const target = params.commitment ?? "finalized";
  const pollMs = params.pollMs ?? 1_500;
  const timeoutMs = params.timeoutMs ?? 180_000;
  const now = params.now ?? Date.now;
  const started = now();
  while (true) {
    if (params.signal?.aborted) fail("confirmation wait was cancelled");
    const [status] = await getSolanaSignatureStatuses(rpc, [params.signature]);
    if (status) {
      if (status.err !== null && status.confirmationStatus === "finalized") {
        throw new SolanaTransactionFailedError(params.signature, status.err);
      }
      if (
        status.err === null &&
        status.confirmationStatus &&
        CONFIRMATION_RANK[status.confirmationStatus] >=
          CONFIRMATION_RANK[target]
      ) {
        return { slot: status.slot };
      }
    } else if (params.lastValidBlockHeight !== undefined) {
      if (
        await isSolanaSignatureExpired(
          rpc,
          params.signature,
          params.lastValidBlockHeight,
        )
      ) {
        throw new SolanaTransactionExpiredError(params.signature);
      }
    }
    if (now() - started > timeoutMs) {
      fail(
        `transaction ${params.signature} was not ${target} within ${Math.round(timeoutMs / 1000)}s`,
      );
    }
    await sleep(pollMs, params.signal);
  }
}
